import JoiDate from "@joi/date";
import { DateSchema, NumberSchema, StringSchema, LanguageMessages } from "joi";
import * as JoiBase from "joi/lib/index"; // Using it for enabling manifest and use any.describe()
import moment from "moment";

import { Address, Community, Zone } from "@/types/api-types";

const validateOnlyOnBack = !!process.env.NEXT_PUBLIC_SHOULD_VALIDATE_ONLY_ON_BACKEND;

const messages: LanguageMessages = {
  "any.required": "Field is required",

  "string.base": "Field must be text",
  "string.empty": "Field cannot be empty",
  "string.min": "Field must be at least {{#limit}} character(s)",
  // "string.max": "Field must be less then {{#limit}} character(s)",
  "string.max": "Field cannot exceed {{#limit}} character(s)",
  "string.email": "Field must be a valid email address",

  "number.base": "Value must be a number",
  "number.integer": "Value must be a whole number",
  "number.min": "Value must be at least {{#limit}}",
  "number.max": "Value cannot be greater than {{#limit}}",

  "date.base": "Field must be a valid date",
  "date.format": "Invalid date format",

  "array.min": "Field must contain at least {{#limit}} item(s)",
};

const JoiBaseWithDates = JoiBase.extend(JoiDate) as typeof JoiBase & {
  date(): DateSchema & { format(format: string | string[]): DateSchema };
};

let Joi = JoiBaseWithDates.defaults(schema => {
  if (schema.type === "string") {
    schema = (schema as StringSchema).trim().empty(["", null]).default(null);
  }
  if (schema.type === "number") {
    schema = (schema as NumberSchema).empty(["", null]).default(null);

    // Disable Number.isSafeInteger check and number.unsafe message, instead use min max limits
    schema = (schema as NumberSchema).unsafe(true);
  }

  if (schema.type === "date") {
    schema = (schema as DateSchema).empty(["", null]).default(null);
  }

  // Apply messages **after modifications**
  schema = schema.messages(messages);

  schema = schema.options({ stripUnknown: true });

  return schema;
});

Joi = Joi.extend(joi => ({
  type: "array",
  base: joi.array(),
  overrides: {
    required() {
      return (
        this.$_super
          // @ts-expect-error // TODO Definition
          .required()
          .min(1)
          .messages({ ...messages, "array.min": "Field is required" })
      );
    },
  },
}));

const allowAny = Joi.any().allow(null, "").optional(); // allows null or empty string too

export const addressEditorSchemaJoi = validateOnlyOnBack
  ? allowAny
  : Joi.object<Address>({
      id: Joi.number().optional(),
      zone_id: Joi.number().required(),
      program_id: Joi.number().required(),
      street_full_name: Joi.string().required().max(255),
      street_number: Joi.string().required().max(10),
      // street_number: Joi.string().required(),
      postal_code: Joi.string()
        .optional()
        .trim()
        .pattern(/^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z] ?\d[ABCEGHJ-NPRSTV-Z]\d$/i)
        .messages({
          ...messages,
          "string.pattern.base": 'Postal code must match the format "A1A 1A1"',
        }),
      city: Joi.string().required().max(100),
      curbside_households: Joi.number().required().integer().min(1).max(1000),
      // curbside_households: Joi.number().required().integer(),
      lat: Joi.number()
        // .required()
        .optional()
        .min(-90)
        .max(90)
        .messages({
          ...messages,
          "number.min": "Latitude must be between −90 and 90 degrees",
          "number.max": "Latitude must be between −90 and 90 degrees",
        }),
      // lat: Joi.number().required(),
      lon: Joi.number()
        // .required()
        .optional()
        .min(-180)
        .max(180)
        .messages({
          ...messages,
          "number.min": "Longitude must be between −180 and 180 degrees",
          "number.max": "Longitude must be between −180 and 180 degrees",
        }),
      // lon: Joi.number().required(),
      unit: Joi.string().optional().max(50),
      notes: Joi.string().optional(),
      collection_route: Joi.string().optional().max(20),
      collection_household_start_date: Joi.date()
        .optional()
        .custom((value, helpers) => {
          const date = moment(value);
          if (!date.isValid()) return helpers.error("date.format");
          return date.utc().format("YYYY-MM-DD");
        }),
    });

export const communityEditorSchemaJoi = validateOnlyOnBack
  ? allowAny
  : Joi.object<Community>({
      id: Joi.number().optional(),
      name: Joi.string().required().min(3).max(255),
      // name: Joi.string().required(),
      program_id: Joi.number().required(),
      description: Joi.string().optional(),
      is_direct: Joi.boolean().optional(),
    });

export const zoneEditorSchemaJoi = validateOnlyOnBack
  ? allowAny
  : Joi.object<Zone & { file?: File }>({
      id: Joi.number(),
      community_id: Joi.number().required(),
      code: Joi.number().required().min(1).max(2000000000).required(),
      // code: Joi.number().required(),
      name: Joi.string().optional().max(255),
      schedules: Joi.array()
        .sparse(true)
        .items(
          Joi.object({
            // id: Joi.number().optional(),
            // zone_id: Joi.number().optional(),
            type_id: Joi.number().required(),
            frequency_id: Joi.number().required(),
            // date_start: Joi.date().required(),
            date_start: Joi.date()
              .required()
              .custom((value, helpers) => {
                const date = moment(value);
                if (!date.isValid()) return helpers.error("date.format");
                return date.utc().format("YYYY-MM-DD");
              }),
          }) /* .empty({
        type_id: "",
        frequency_id: "",
        date_start: "",
      }) */,
        )
        .custom(arr => arr.filter((item: unknown) => !!item)) // Remove empty items
        .optional(),
      /* file: Joi.any().when(Joi.ref("$isCreating"), {
    is: true,
    then: Joi.required().messages({ "any.required": "File is required" }),
    otherwise: Joi.optional(),
  }), */
      file: Joi.any()
        .optional()
        .messages({ ...messages, "any.required": "File is required" }),
    });

export const userEditorSchemaJoi = validateOnlyOnBack
  ? allowAny
  : Joi.object({
      id: Joi.number().optional(),
      name: Joi.string().required().min(3).max(128),
      // name: Joi.string().required(),
      email: Joi.string().required().email(),
      role_id: Joi.number().required(),
      // program_ids: Joi.array().items(Joi.number()).min(1),
      program_ids: Joi.array().required().items(Joi.number()),
    });

export const importMultipleAddressesSchemaJoi = validateOnlyOnBack
  ? allowAny
  : Joi.object({
      program_id: Joi.number().required(),
      file: Joi.any()
        .required()
        .messages({ ...messages, "any.required": "File is required" }),
    });
